import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getEduProApplications } from '../services/api';
import { EduProContract } from './EduProContract';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Loader2, FileText, ShieldCheck, CheckCircle, AlertTriangle, RefreshCw,
  Wallet, Clock, FileSignature, User, Users,
} from 'lucide-react';

const STATUS = {
  draft: { label: 'Borrador', cls: 'bg-slate-100 text-slate-700' },
  approved: { label: 'Aprobado', cls: 'bg-emerald-100 text-emerald-700' },
  manual_review: { label: 'En revisión', cls: 'bg-amber-100 text-amber-700' },
  active: { label: 'Activo', cls: 'bg-[#0A2463] text-white' },
};
const CHECK_LABELS = {
  document_ocr: 'Documento (OCR)', liveness: 'Prueba de vida', face_match: 'Biometría facial',
  registry: 'Registro oficial', tax: 'Validación fiscal', aml: 'Listas AML/PEP', credit_bureau: 'Buró de crédito',
};

const money = (v, currency) =>
  new Intl.NumberFormat(currency === 'COP' ? 'es-CO' : 'es-MX', { style: 'currency', currency: currency || 'MXN', minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const SubjectStatus = ({ title, icon: Icon, data }) => {
  if (!data) return <div className="border border-slate-200 rounded-xl p-4 text-sm text-slate-400">Sin verificación</div>;
  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-[#0A2463]" /><span className="font-semibold text-sm text-slate-800">{title}</span></div>
        <Badge className={data.identity_ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{data.identity_ok ? 'Verificado' : 'Revisión'}</Badge>
      </div>
      <div className="space-y-1.5">
        {(data.checks || []).map((c) => (
          <div key={c.id} className="flex items-center justify-between text-xs">
            <span className="text-slate-500">{CHECK_LABELS[c.id] || c.id}</span>
            <span className={`flex items-center gap-1 font-medium ${c.status === 'passed' ? 'text-emerald-600' : 'text-amber-600'}`}>
              {c.status === 'passed' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {c.score != null ? `${c.score}%` : (c.status === 'passed' ? 'OK' : 'Revisar')}
            </span>
          </div>
        ))}
      </div>
      {data.credit && <p className="text-[11px] text-slate-400 mt-3 pt-2 border-t border-slate-100">Score: <strong className="text-slate-600">{data.credit.score}</strong> · Perfil: <strong className="text-slate-600 capitalize">{data.credit.tier}</strong></p>}
    </div>
  );
};

export const EduProAdminPanel = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('all');
  const [status, setStatus] = useState('all');
  const [contractId, setContractId] = useState(null);
  const [verifApp, setVerifApp] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (country !== 'all') params.country = country;
    if (status !== 'all') params.status = status;
    getEduProApplications(params)
      .then((r) => setApps(r.data || []))
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  }, [country, status]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const s = { total: apps.length, approved: 0, review: 0, active: 0, financed: { MXN: 0, COP: 0 } };
    apps.forEach((a) => {
      if (a.status === 'approved') s.approved += 1;
      if (a.status === 'manual_review') s.review += 1;
      if (a.status === 'active') s.active += 1;
      if (['approved', 'active'].includes(a.status)) s.financed[a.currency || 'MXN'] += (a.amount_approved || a.principal || 0);
    });
    return s;
  }, [apps]);

  return (
    <div className="space-y-5" data-testid="edupro-admin-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { icon: FileText, box: 'bg-blue-100', ic: 'text-blue-600', value: stats.total, label: 'Solicitudes' },
          { icon: CheckCircle, box: 'bg-emerald-100', ic: 'text-emerald-600', value: stats.approved, label: 'Aprobadas' },
          { icon: Clock, box: 'bg-amber-100', ic: 'text-amber-600', value: stats.review, label: 'En revisión' },
          { icon: FileSignature, box: 'bg-indigo-100', ic: 'text-indigo-600', value: stats.active, label: 'Contratos firmados' },
        ].map((c, i) => (
          <Card key={i}><CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.box}`}><c.icon className={`w-5 h-5 ${c.ic}`} /></div>
              <div><p className="text-2xl font-bold">{c.value}</p><p className="text-xs text-slate-600">{c.label}</p></div>
            </div>
          </CardContent></Card>
        ))}
        <Card><CardContent className="pt-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-pink-100"><Wallet className="w-5 h-5 text-pink-600" /></div>
            <div>
              <p className="text-sm font-bold text-[#0A2463] leading-tight">{money(stats.financed.MXN, 'MXN')}</p>
              <p className="text-sm font-bold text-[#0A2463] leading-tight">{money(stats.financed.COP, 'COP')}</p>
              <p className="text-xs text-slate-600">Financiado</p>
            </div>
          </div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-40">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="edupro-filter-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              <SelectItem value="MX">🇲🇽 México</SelectItem>
              <SelectItem value="CO">🇨🇴 Colombia</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="edupro-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="approved">Aprobado</SelectItem>
              <SelectItem value="manual_review">En revisión</SelectItem>
              <SelectItem value="active">Activo (firmado)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="edupro-admin-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Actualizar</Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-7 h-7 animate-spin text-[#0A2463] mx-auto" /></div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12 text-slate-500">No hay solicitudes EduPro con estos filtros.</div>
      ) : (
        <div className="table-scroll border border-slate-200 rounded-xl">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Solicitante</TableHead>
              <TableHead>Aval</TableHead>
              <TableHead>País</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead className="hidden sm:table-cell">Fecha</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {apps.map((a) => {
                const st = STATUS[a.status] || { label: a.status, cls: 'bg-slate-100 text-slate-700' };
                return (
                  <TableRow key={a.application_id} data-testid={`edupro-admin-row-${a.application_id}`}>
                    <TableCell className="font-medium">{a.applicant?.full_name || '—'}<div className="text-[11px] text-slate-400">{a.applicant?.national_id}</div></TableCell>
                    <TableCell className="text-sm">{a.guarantor?.full_name || '—'}</TableCell>
                    <TableCell>{a.country === 'CO' ? '🇨🇴 CO' : '🇲🇽 MX'}</TableCell>
                    <TableCell className="text-right font-semibold">{money(a.amount_approved || a.principal, a.currency)}</TableCell>
                    <TableCell><Badge className={st.cls}>{st.label}</Badge></TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-slate-500">{fmtDate(a.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 justify-end">
                        {a.verification && (
                          <Button size="sm" variant="ghost" className="text-xs" onClick={() => setVerifApp(a)} data-testid={`edupro-admin-verif-${a.application_id}`}>
                            <ShieldCheck className="w-4 h-4" />
                          </Button>
                        )}
                        {['approved', 'active'].includes(a.status) && (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => setContractId(a.application_id)} data-testid={`edupro-admin-contract-${a.application_id}`}>
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {contractId && <EduProContract applicationId={contractId} readOnly onClose={() => setContractId(null)} />}

      {verifApp && (
        <Dialog open={true} onOpenChange={() => setVerifApp(null)}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-[#0A2463]">Verificación · {verifApp.applicant?.full_name}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <SubjectStatus title="Solicitante" icon={User} data={verifApp.verification?.applicant} />
              <SubjectStatus title="Aval / Co-deudor" icon={Users} data={verifApp.verification?.guarantor} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EduProAdminPanel;
